import Analytics16 from '@carbon/icons-react/es/analytics/16';
import Dashboard16 from '@carbon/icons-react/es/dashboard/16';
import EventsAlt16 from '@carbon/icons-react/es/events--alt/16';
import Home16 from '@carbon/icons-react/es/home/16';
import ListChecked16 from '@carbon/icons-react/es/list--checked/16';
import Meter16 from '@carbon/icons-react/es/meter/16';
import Task16 from '@carbon/icons-react/es/task/16';
import Template16 from '@carbon/icons-react/es/template/16';
import Timer16 from '@carbon/icons-react/es/timer/16';
import React, { useMemo } from 'react';
import { TimesheetAccessState } from 'api/account';
import { PermissionSetType } from 'api/models';
import { TimesheetApprovalType } from 'api/timesheets/configuration/configuration';
import { UserWaiting16 } from 'components/atoms/icons/user-waiting';
import { PermissionRuleName, RoutePaths } from 'constant';
import {
  useIsCurrentUserCollaborator,
  usePermissionForRule,
  useUserPermissionSetType,
} from 'hooks/permissions';
import { useBillingFeatureFlag, useIsAccountActive } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useUser } from 'hooks/use-user';
import { useProjectsQuery } from 'queries';
import { useAccountDataQuery } from 'queries/account';
import { useResourceManagementConfigQuery } from 'queries/resource-management-configuration';
import { useTimesheetConfiguration } from 'queries/timesheets/configuration';
import { LeftNavMenus, LeftNavMenuType } from './types';
import { canShowMenuItem } from './utils';

export const usePrimaryMenuItems = (): LeftNavMenus => {
  const isAccActive = useIsAccountActive();
  const { data: accountData } = useAccountDataQuery();

  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );
  const { isAllowedForCurrentUser } = usePermissionForRule();
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const hasAccessToTimesheetMenu =
    isAllowedForCurrentUser(PermissionRuleName.TimesheetPage) &&
    accountData?.timeSheetAccess !== TimesheetAccessState.Disabled;

  const { data } = useProjectsQuery();
  const currentUser = useUser();

  const projectIOwn =
    data?.filter(
      (project) => project?.projectOwner?.userId === currentUser?.user?.userId,
    ) || [];
  const { data: configuration } = useTimesheetConfiguration();
  const isProjectOwnerInPOFlow =
    projectIOwn?.length > 0 ||
    configuration?.approvalSetting === TimesheetApprovalType.ProjectOwner;

  const isCollaborator = useIsCurrentUserCollaborator();

  const { data: resourceManagementConfig } = useResourceManagementConfigQuery();
  // to be revisited when view and edit access are differentiated
  const isEnabledForCollaborator =
    resourceManagementConfig?.accessConfig?.collaboratorAccessMeta
      ?.hasViewAccess ?? false;

  const isBillingEnabled = useBillingFeatureFlag();
  const menus: LeftNavMenus = useMemo(() => {
    return [
      {
        type: LeftNavMenuType.Leaf,
        path: RoutePaths.home,
        key: 'home',
        icon: <Home16 />,
      },
      {
        type: LeftNavMenuType.WithChildren,
        icon: <Dashboard16 />,
        key: 'dashboard_and_reports',
        children: [
          {
            type: LeftNavMenuType.Leaf,
            path: RoutePaths.dashboard,
            key: 'dashboard',
            icon: <Meter16 />,
            hasPermission: isAllowedForCurrentUser(
              PermissionRuleName.DashboardPage,
            ),
          },
          {
            type: LeftNavMenuType.Leaf,
            path: RoutePaths.reports,
            key: 'reports',
            icon: <Analytics16 />,
            hasPermission: isAllowedForCurrentUser(
              PermissionRuleName.ReportsPage,
            ),
            children: [],
          },
        ],
      },

      {
        type: LeftNavMenuType.Leaf,
        path: RoutePaths.projects,
        key: 'projects',
        icon: <Task16 />,
      },
      {
        type: LeftNavMenuType.Leaf,
        path: RoutePaths.tasks,
        key: 'tasks',
        icon: <ListChecked16 />,
      },
      {
        type: LeftNavMenuType.WithChildren,
        key: 'timesheet_and_resource_management',
        icon: <UserWaiting16 />,
        children: [
          {
            type: LeftNavMenuType.Leaf,
            path: RoutePaths.resourceManagement,
            key: isCollaborator ? 'manage_capacity' : 'resource_management',
            icon: <EventsAlt16 />,
            hasPermission:
              isAllowedForCurrentUser(
                PermissionRuleName.ResourceManagementPage,
              ) ||
              (isCollaborator && isEnabledForCollaborator),
          },
          {
            type: LeftNavMenuType.Leaf,
            path: RoutePaths.timesheets,
            key: 'timesheets',
            icon: <Timer16 />,
            hasPermission:
              !isTimesheetFeatureEnabled ||
              hasAccessToTimesheetMenu ||
              isProjectOwnerInPOFlow,
            featureEnabled: isTimesheetFeatureEnabled || isAccountAdmin,
          },
        ],
      },
      {
        type: LeftNavMenuType.Leaf,
        path: RoutePaths.templates,
        key: 'templates',
        icon: <Template16 />,
        hasPermission: isAllowedForCurrentUser(PermissionRuleName.TemplatePage),
      },
    ];
  }, [
    isAllowedForCurrentUser,
    isCollaborator,
    isEnabledForCollaborator,
    isTimesheetFeatureEnabled,
    hasAccessToTimesheetMenu,
    isProjectOwnerInPOFlow,
    isAccountAdmin,
  ]);

  const accessibleMenus = useMemo(
    () =>
      menus
        .map((item) => {
          if (item.type === LeftNavMenuType.Leaf) {
            return canShowMenuItem(item) ? item : undefined;
          }
          const showableChildren = item.children.filter((menuItem) =>
            canShowMenuItem(menuItem),
          );

          return showableChildren.length > 0
            ? {
                ...item,
                children: showableChildren,
              }
            : undefined;
        })
        .filter(Boolean) as LeftNavMenus,
    [menus],
  );

  return !isBillingEnabled || isAccActive ? accessibleMenus : [];
};
